/**
 * The fourth tab: everything the desktop keeps in chrome a phone has no room
 * for.
 *
 * On the desktop these live in the layout bar, a right-click menu and a vertical
 * strip — three surfaces that all assume a pointer. None of them survives here,
 * so what they held collects in one list.
 *
 * **The arrangements grid is deliberately absent.** Nine geometries of rows and
 * columns describe a screen that shows several charts at once, and this shell
 * shows one at a time by construction. Switching *layout* is still here, because
 * that is a different question — which set of charts, not how they are tiled.
 */

import { useEffect, useState } from "react";
import type { Layout, Theme, UserWorkspace } from "@qufolio/model";

import { signOut } from "../lib/session";
import { actingAs, setActingAs } from "../lib/act-as";
import { accountLabel, accountTitle, fetchMe, type Me } from "../api/me";

export interface MobileMoreProps {
  workspace: UserWorkspace;
  layoutIndex: number;
  onSelectLayout: (index: number) => void;
  themes: readonly Theme[];
  onOpenTheme: () => void;
  /** Signed-in identity, when there is one. */
  email?: string;
  /** How many charts the current layout has, stated because the tab badge is terse. */
  chartCount: number;
  /**
   * Opens the AI chat full screen (docs/features/chat.md). Absent — the brand
   * without the agent, a plan without `feat:agent`, no quevee — draws no row.
   */
  onOpenAi?: () => void;
}

const HEAD: React.CSSProperties = {
  padding: "16px 14px 6px",
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "14px",
  minHeight: 52,
  width: "100%",
  textAlign: "left",
  border: 0,
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  font: "inherit",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
};

const VALUE: React.CSSProperties = {
  color: "var(--qf-text-muted)",
  fontSize: 13,
  maxWidth: "50%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export function MobileMore({
  workspace,
  layoutIndex,
  onSelectLayout,
  themes,
  onOpenTheme,
  email,
  chartCount,
  onOpenAi,
}: MobileMoreProps) {
  const theme = themes.find((t) => t.id === workspace.themeRef);

  /**
   * Who the **server** says we are, and which accounts it will let us act as.
   *
   * Asked rather than assumed, for `api/me`'s reason: roles live in the
   * database and `X-Act-As` is judged per request (D31), so a client deciding
   * for itself would offer a switcher that 403s. Nothing renders until it
   * answers, which also means a non-admin never sees the section exists.
   */
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchMe().then((found) => {
      if (alive) setMe(found);
    });
    return () => {
      alive = false;
    };
  }, []);

  const viewingAs = me?.actingAs ?? actingAs();

  /**
   * Switching account reloads the page, exactly as the desktop switcher does.
   *
   * Not laziness: the workspace, the journal, both chart sockets and the
   * screener all load on mount and every one of them is now showing the wrong
   * account's data. A reload is one line and correct; threading invalidation
   * through all of them is a lot of code whose failure mode is a chart still
   * showing the previous user's symbol.
   */
  const switchTo = (target: string | null) => {
    setActingAs(target);
    window.location.reload();
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {/* First, because it is the one row here that is a place rather than a
          setting — a chevron, not a tick. */}
      {onOpenAi && (
        <>
          <div style={HEAD}>Assistant</div>
          <button style={ROW} onClick={onOpenAi} aria-label="AI — ask about this chart or screen">
            <span style={{ flex: 1 }}>AI</span>
            <span style={VALUE}>Ask about this chart</span>
            <span style={{ color: "var(--qf-text-muted)", width: 14, textAlign: "right" }}>›</span>
          </button>
        </>
      )}
      <div style={HEAD}>Layout</div>
      {workspace.layouts.map((layout: Layout, i) => (
        <button
          key={layout.id}
          style={ROW}
          onClick={() => onSelectLayout(i)}
          // Named, because the visible label is the user's own layout name and
          // the tick beside it is the only other thing distinguishing these
          // rows — a screen reader would otherwise read five identical buttons.
          aria-label={`Layout ${layout.name || "Untitled"}`}
          aria-current={i === layoutIndex}
        >
          <span style={{ flex: 1 }}>{layout.name || "Untitled"}</span>
          <span style={VALUE}>
            {i === layoutIndex ? `${chartCount} chart${chartCount === 1 ? "" : "s"}` : ""}
          </span>
          {/* A tick rather than a chevron on the current one: these are a
              single choice from a list, not five places to navigate to. */}
          <span style={{ color: "var(--qf-accent)", width: 14, textAlign: "right" }}>
            {i === layoutIndex ? "✓" : ""}
          </span>
        </button>
      ))}

      <div style={HEAD}>Appearance</div>
      <button style={ROW} onClick={onOpenTheme}>
        <span style={{ flex: 1 }}>Theme</span>
        <span style={VALUE}>{theme?.name ?? "Default"}</span>
        <span style={{ color: "var(--qf-text-muted)" }}>›</span>
      </button>

      {/* Only for an admin with somebody to act as. The desktop hides it on the
          same test, and for the same reason: an empty switcher is a control
          that exists to be refused. */}
      {me?.isAdmin && me.testUsers.length > 0 && (
        <>
          <div style={HEAD}>View as</div>
          {/* A row per account rather than a `<select>`. The desktop's dropdown
              is right for a menu beside a mouse; here the list is short, the
              rows are already the tab's idiom, and a native picker on a phone
              is a full-screen modal for what is usually a two-item choice. */}
          {["", ...me.testUsers].map((key) => {
            const on = (viewingAs ?? "") === key;
            return (
              <button
                key={key || "self"}
                style={ROW}
                aria-current={on}
                onClick={() => switchTo(key || null)}
              >
                <span style={{ flex: 1 }}>{key || "Myself"}</span>
                {key === "" && <span style={VALUE}>{me.actorKey}</span>}
                <span style={{ color: "var(--qf-accent)", width: 14, textAlign: "right" }}>
                  {on ? "✓" : ""}
                </span>
              </button>
            );
          })}
        </>
      )}

      <div style={HEAD}>Account</div>
      {/* Whose data is on screen, said plainly. The desktop has a full-width
          banner for this — see `ActingAs` — and a phone has no room for one, so
          it is stated here in the colour that banner uses. Losing track of it
          is how an admin edits the wrong person's journal. */}
      {viewingAs && (
        <div style={{ ...ROW, cursor: "default", background: "#8a5a00", color: "#fff" }}>
          <span style={{ flex: 1 }}>Viewing {viewingAs}&apos;s data</span>
          <button
            style={{ ...ROW, width: "auto", padding: "4px 10px", minHeight: 0, border: "1px solid rgba(255,255,255,0.35)", borderRadius: 4, background: "rgba(255,255,255,0.18)", color: "#fff" }}
            onClick={() => switchTo(null)}
          >
            Stop
          </button>
        </div>
      )}
      {/* The server's answer first: it says who is signed in, which is also
          the only one that stays right while acting as somebody else — shown
          as a name, with the key behind it on hover. `email` is the caller's
          hint for a build with no `/auth/me` behind it. */}
      {(me?.actorKey || email) && (
        <div style={{ ...ROW, cursor: "default" }} title={me ? accountTitle(me) : undefined}>
          <span style={{ flex: 1 }}>Signed in</span>
          <span style={VALUE}>{me?.actorKey ? accountLabel(me) : email}</span>
        </div>
      )}
      <button style={ROW} onClick={() => void signOut()}>
        <span style={{ flex: 1 }}>Sign out</span>
      </button>
    </div>
  );
}
